import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { getOwner } from '../api/owner'
import { Monogram } from '../components/Monogram'
import { NameField } from '../components/NameField'
import { PrimaryButton, IconButton } from '../components/buttons'
import { useSession } from '../session/context'

/**
 * Ronde 4 en 5 — "Hoe kennen we jou?". Op mobiel één kolom met de knop
 * onderaan, vanaf lg hetzelfde beeld links en het naamveld rechts.
 */
export function WhoAreYouPage() {
  const { event, guest, saveGuest } = useSession()
  const navigate = useNavigate()

  const [name, setName] = useState(guest?.displayName ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = name.trim()
  const hero = event.heroMedia.at(0)

  /**
   * Zit er een eigenaar achter deze browser?
   *
   * Meestal niet — dit scherm is voor gasten met een QR-code. Maar de eigenaar
   * bekijkt zijn eigen feest ook, en die stond hier zijn eigen naam in te typen
   * alsof we hem niet kenden. Geen sessie is hier dus een gewone uitkomst en
   * geen fout: niet opnieuw proberen, en nergens een melding.
   */
  const owner = useQuery({
    queryKey: ['owner', 'me'],
    queryFn: () => getOwner(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  async function enter(displayName: string, useOwnerAccount = false) {
    if (saving) {
      return
    }

    setSaving(true)
    setError(null)

    try {
      await saveGuest(displayName, useOwnerAccount)
      void navigate('../album', { relative: 'path', replace: true })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Opslaan lukte niet.')
      setSaving(false)
    }
  }

  function submit(submission: FormEvent) {
    submission.preventDefault()

    if (trimmed) {
      void enter(trimmed)
    }
  }

  return (
    <main className="flex min-h-full flex-col lg:h-full lg:flex-row lg:overflow-hidden">
      {/* Alleen op desktop: het ontwerp zet er de eerste hero neer, zonder
          carousel — dit scherm gaat over één ding en dat is het veld rechts. */}
      <div className="placeholder-warm hidden lg:block lg:h-full lg:w-1/2 lg:shrink-0">
        {hero && <img src={hero.webUrl} alt="" className="h-full w-full object-cover" />}
      </div>

      <form
        onSubmit={submit}
        className="flex flex-1 flex-col px-7 lg:relative lg:justify-center lg:px-24"
      >
        <header className="flex items-center justify-between pt-6 lg:absolute lg:top-9 lg:left-24 lg:justify-start lg:gap-3.5 lg:pt-0">
          <IconButton
            label="Terug"
            onClick={() => navigate('..', { relative: 'path' })}
            className="lg:size-11"
          >
            ←
          </IconButton>

          <Monogram coupleNames={event.coupleNames} size={20} />

          {/* Houdt het monogram op mobiel gecentreerd. */}
          <span className="w-10 lg:hidden" />
        </header>

        <div className="pt-12 lg:pt-0">
          <h1 className="font-display text-[42px] leading-[1.12] lg:text-[60px] lg:leading-[1.08]">
            Hoe kennen
            <br />
            we jou?
          </h1>

          <p className="text-ink-muted mt-3.5 max-w-[300px] text-base leading-[1.5] text-pretty lg:mt-4.5 lg:max-w-[430px] lg:text-lg">
            Zet je naam erbij zoals {event.coupleNames} je noemen. Zo weten ze straks van wie welke
            foto is.
          </p>
        </div>

        {/*
          De snelweg voor de eigenaar. Boven het veld en niet erin: een
          voorgevulde naam ziet eruit als iets dat je nog moet nakijken, terwijl
          dit juist de bedoelde weg is zodra hij er staat. De naam komt van de
          server uit de sessiecookie — wat hier staat is alleen wat je leest.
        */}
        {owner.data && (
          <div className="border-line-card bg-surface rounded-card mt-8 border p-4 lg:mt-12 lg:max-w-[460px] lg:p-5">
            <p className="text-ink-faint text-[11px] font-semibold tracking-[0.09em] uppercase">
              Je bent ingelogd
            </p>
            <p className="mt-1 truncate text-base font-semibold">{owner.data.displayName}</p>

            <button
              type="button"
              disabled={saving}
              onClick={() => void enter(owner.data.displayName, true)}
              className="bg-accent text-accent-ink mt-3.5 h-12 w-full cursor-pointer rounded-full text-[15px] font-semibold transition hover:brightness-95 disabled:opacity-60"
            >
              Ga verder als {owner.data.displayName}
            </button>

            <p className="text-ink-soft mt-3 text-center text-[13px] leading-[1.45]">
              Of vul hieronder een andere naam in.
            </p>
          </div>
        )}

        <div className="pt-8 lg:max-w-[460px] lg:pt-12">
          <NameField value={name} onChange={setName} size="lg" autoFocus={!owner.data} />

          <p
            className={
              'text-ink-faint mt-3 text-sm transition-opacity ' +
              (trimmed ? 'opacity-100' : 'opacity-0')
            }
          >
            Zichtbaar bij elke foto die je uploadt.
          </p>
        </div>

        {error && (
          <p role="alert" className="text-accent mt-2 text-sm">
            {error}
          </p>
        )}

        {/* Duwt de knop op mobiel naar de onderrand, zoals in ronde 4A. Zodra
            het toetsenbord opengaat krimpt de viewport en schuift hij mee. */}
        <div className="min-h-6 flex-1 lg:hidden" />

        <div className="pb-9 lg:mt-10 lg:max-w-[420px] lg:pb-0">
          <PrimaryButton type="submit" disabled={!trimmed || saving}>
            {saving ? 'Even opslaan…' : 'Ga verder'}
          </PrimaryButton>
        </div>
      </form>
    </main>
  )
}
